import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { logWarn } from "../lib/log";

const ThemeContext = createContext(null);

/**
 * Two modes only:
 *  - "dark" : default when no preference is stored
 *  - "light": explicit user choice
 *
 * Never reads prefers-color-scheme. Persists to localStorage as "theme_mode".
 * `resolved` mirrors `mode` (kept for backwards compatibility with existing consumers).
 */
function readStoredMode() {
  try {
    const stored = localStorage.getItem("theme_mode");
    if (stored === "light" || stored === "dark") return stored;
  } catch (e) {
    logWarn("[theme] localStorage read failed", e);
  }
  return "dark";
}

export function ThemeProvider({ children }) {
  const [mode, setMode] = useState(readStoredMode);
  // resolved == mode (kept as separate field for older components that read it)
  const resolved = mode;

  useEffect(() => {
    try {
      localStorage.setItem("theme_mode", mode);
    } catch (e) {
      logWarn("[theme] localStorage write failed", e);
    }
    const html = document.documentElement;
    html.classList.toggle("light", mode === "light");
    html.classList.toggle("dark", mode === "dark");
  }, [mode]);

  const cycle = useCallback(() => {
    setMode((m) => (m === "dark" ? "light" : "dark"));
  }, []);

  const value = useMemo(() => ({ mode, resolved, setMode, cycle }), [mode, resolved, cycle]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
